import { Vector3 } from 'three';
import {
  CoordinateSystem,
  LocalFrameAxes,
  deriveAxesFromDefinition,
  normalizeCoordinateSystemId
} from './local-frame.util';

interface FramedVectors {
  forceComponent: number[];
  momentComponent: number[];
  coordinateSystemId?: string | null;
}

function rotateToGlobal(axes: LocalFrameAxes, v: number[]): Vector3 {
  return new Vector3(
    axes.ex[0] * v[0] + axes.ey[0] * v[1] + axes.ez[0] * v[2],
    axes.ex[1] * v[0] + axes.ey[1] * v[1] + axes.ez[1] * v[2],
    axes.ex[2] * v[0] + axes.ey[2] * v[1] + axes.ez[2] * v[2]
  );
}

/**
 * Force and moment as they should be drawn: rotated out of the item's frame
 * into global. An unresolvable or degenerate frame falls back to the raw
 * vectors, so a broken reference looks like it did before rather than
 * vanishing - the emitter is what refuses to run.
 *
 * Systems and arrows are both in the model's own coordinates, so the frame's
 * axes apply as stored.
 */
export function resolveDisplayVectors(
  item: FramedVectors,
  systems: CoordinateSystem[]
): { force: Vector3; moment: Vector3 } {
  const raw = {
    force: new Vector3(...(item.forceComponent as [number, number, number])),
    moment: new Vector3(...(item.momentComponent as [number, number, number]))
  };

  const id = normalizeCoordinateSystemId(item.coordinateSystemId);
  if (!id) return raw;

  const system = (systems ?? []).find((candidate) => candidate.id === id);
  if (!system) return raw;

  const axes = deriveAxesFromDefinition(system.definition);
  if (!axes) return raw;

  return {
    force: rotateToGlobal(axes, item.forceComponent),
    moment: rotateToGlobal(axes, item.momentComponent)
  };
}
